import React, { useMemo } from 'react';
import { Box, Typography, Avatar, Stack, ButtonBase } from '@mui/material';
import {
    LoginRounded,
    LogoutRounded,
    LocalCafeRounded,
    ReplayRounded,
    TimerOffRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';

import {
    DASH,
    EVENT_TONES,
    resolvePhotoUrl,
    getInitials,
    avatarTint,
} from './dashboardTokens';
import { PanelBody, EmptyState, RowSkeleton, Eyebrow } from './Surface';

const DAYJS_LOCALE = { SK: 'sk', EN: 'en', CZ: 'cs' };

const KIND_ICON = {
    arrival: LoginRounded,
    departure: LogoutRounded,
    break_start: LocalCafeRounded,
    break_end: ReplayRounded,
    auto_end: TimerOffRounded,
};

// Krátke, bezrodové pomenovanie — čitateľné na jeden pohľad a sedí na každého.
const KIND_LABEL_KEY = {
    arrival: 'arrival',
    departure: 'departure',
    break_start: 'breakStart',
    break_end: 'breakEnd',
    auto_end: 'autoEnd',
};

// Súhrn dňa zhŕňa udalosti do troch tried — tých istých, ktoré nesú farbu.
const SUMMARY_GROUPS = [
    { key: 'arrivals', kinds: ['arrival'], tone: EVENT_TONES.arrival, icon: LoginRounded },
    { key: 'breaks', kinds: ['break_start'], tone: EVENT_TONES.break_start, icon: LocalCafeRounded },
    { key: 'departures', kinds: ['departure', 'auto_end'], tone: EVENT_TONES.departure, icon: LogoutRounded },
];

const dayLabel = (dateStr, language, t) => {
    const locale = DAYJS_LOCALE[language] || 'sk';
    const today = dayjs().format('YYYY-MM-DD');
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    if (dateStr === today) return t('pages.dashboard.feed.today');
    if (dateStr === yesterday) return t('pages.dashboard.feed.yesterday');
    return dayjs(dateStr, 'YYYY-MM-DD').locale(locale).format('D. MMMM');
};

/**
 * Súhrn posledného dňa — zároveň legenda k farbám nižšie. Tri čísla stačia na
 * otázku „čo sa dnes dialo“ bez toho, aby človek čítal jednotlivé riadky.
 */
const DaySummary = ({ events, label, t }) => {
    const counts = useMemo(() => {
        const acc = {};
        events.forEach((e) => { acc[e.kind] = (acc[e.kind] || 0) + 1; });
        return acc;
    }, [events]);

    return (
        <Stack
            direction="row"
            spacing={{ xs: 2.5, sm: 4 }}
            sx={{
                px: 1.25,
                py: 1.5,
                mb: 1,
                borderRadius: `${DASH.radiusSm}px`,
                bgcolor: 'action.hover',
                flexWrap: 'wrap',
            }}
            useFlexGap
        >
            {SUMMARY_GROUPS.map(({ key, kinds, tone, icon: Icon }) => {
                const count = kinds.reduce((sum, k) => sum + (counts[k] || 0), 0);
                return (
                    <Stack key={key} direction="row" spacing={1.25} alignItems="center">
                        <Box
                            sx={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                bgcolor: count > 0 ? tone : 'transparent',
                                border: count > 0 ? 'none' : '1px dashed',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                '& svg': { fontSize: 14, color: count > 0 ? '#fff' : 'text.disabled' },
                            }}
                        >
                            <Icon />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: DASH.display,
                                    fontSize: '1.125rem',
                                    fontWeight: 600,
                                    lineHeight: 1.05,
                                    letterSpacing: '-0.02em',
                                    color: count > 0 ? 'text.primary' : 'text.disabled',
                                    ...DASH.numeric,
                                }}
                            >
                                {count}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', lineHeight: 1.3 }}
                                noWrap
                            >
                                {t(`pages.dashboard.feed.summary.${key}`)}
                            </Typography>
                        </Box>
                    </Stack>
                );
            })}
            <Box sx={{ flex: 1 }} />
            <Eyebrow sx={{ alignSelf: 'center' }}>{label}</Eyebrow>
        </Stack>
    );
};

/**
 * Riadok je bod na časovej osi: čas vľavo, farebný uzol s ikonou na osi,
 * meno a typ udalosti vpravo. Typ nesie farba aj ikona — zámerne dvakrát, aby
 * sa dal prečítať bez čítania.
 */
const EventRow = ({ event, t, onOpen }) => {
    const Icon = KIND_ICON[event.kind] || LoginRounded;
    const tone = EVENT_TONES[event.kind] || EVENT_TONES.departure;
    const photo = resolvePhotoUrl(event.employeePhoto);
    const label = t(`pages.dashboard.feed.kinds.${KIND_LABEL_KEY[event.kind] || 'arrival'}`);

    return (
        <ButtonBase
            onClick={() => onOpen(event)}
            sx={{
                position: 'relative',
                width: '100%',
                textAlign: 'left',
                justifyContent: 'stretch',
                alignItems: 'stretch',
                borderRadius: `${DASH.radiusSm}px`,
                py: 1,
                transition: 'background-color .15s ease',
                '&:hover': { bgcolor: 'action.hover' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                // Os času — vlasová linka pretínajúca celý riadok; uzol ju prekryje.
                // 50px stĺpec s časom + 28px uzol so záporným okrajom → stred osi
                // vychádza na 63 px od ľavého okraja riadka.
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 63,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    bgcolor: 'divider',
                },
                '&:first-of-type::before': { top: '50%' },
                '&:last-of-type::before': { bottom: '50%' },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                sx={{ width: '100%', position: 'relative', zIndex: 1 }}
            >
                <Typography
                    sx={{
                        width: 50,
                        flexShrink: 0,
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                        ...DASH.numeric,
                    }}
                >
                    {dayjs(event.at).format('H:mm')}
                </Typography>

                <Box
                    sx={(theme) => ({
                        position: 'relative',
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        mx: '-1px',
                        borderRadius: '50%',
                        bgcolor: tone,
                        boxShadow: `0 0 0 4px ${theme.palette.background.paper}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& svg': { fontSize: 15, color: '#fff' },
                    })}
                >
                    <Icon />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1, pl: 2 }}>
                    <Typography
                        sx={{
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            lineHeight: 1.3,
                        }}
                        noWrap
                    >
                        {event.employeeName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }} noWrap>
                        <Box component="span" sx={{ color: tone, fontWeight: 650 }}>{label}</Box>
                        {event.workplaceName && (
                            <Box component="span" sx={{ color: 'text.secondary' }}>{` · ${event.workplaceName}`}</Box>
                        )}
                    </Typography>
                </Box>

                <Avatar
                    src={photo || undefined}
                    alt=""
                    sx={{
                        width: 30,
                        height: 30,
                        ml: 1.5,
                        flexShrink: 0,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        color: '#fff',
                        bgcolor: avatarTint(event.employeeName),
                    }}
                >
                    {getInitials(event.employeeName)}
                </Avatar>
            </Stack>
        </ButtonBase>
    );
};

const GhostRows = () => (
    <Stack spacing={2.5} sx={{ opacity: 0.45, pointerEvents: 'none', mb: 3 }}>
        {[68, 52, 60].map((w, i) => (
            <Stack key={i} direction="row" spacing={1.75} alignItems="center">
                <Box sx={{ height: 9, width: 34, borderRadius: 4, bgcolor: 'action.hover', flexShrink: 0 }} />
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        border: '1px dashed',
                        borderColor: 'divider',
                        flexShrink: 0,
                    }}
                />
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ height: 9, width: `${w}%`, borderRadius: 4, bgcolor: 'action.hover', mb: 0.75 }} />
                    <Box sx={{ height: 8, width: `${w - 22}%`, borderRadius: 4, bgcolor: 'action.hover' }} />
                </Box>
            </Stack>
        ))}
    </Stack>
);

/**
 * Nástenka — bežiaci výpis toho, čo sa v tíme deje: príchody, prestávky, odchody.
 * Zoskupené po dňoch, pretože deň je jediné delenie, ktoré tu nesie informáciu.
 */
const ActivityFeed = ({ events, loading, language, t }) => {
    const navigate = useNavigate();

    const groups = useMemo(() => {
        const map = new Map();
        (events || []).forEach((event) => {
            const date = String(event.at).slice(0, 10);
            if (!map.has(date)) map.set(date, []);
            map.get(date).push(event);
        });
        return Array.from(map.entries());
    }, [events]);

    // Riadok je o človeku, nie o dni — otvoríme jeho stránku na tom dni.
    const openEvent = (event) => {
        if (!event.employeeId) return;
        const date = String(event.at).slice(0, 10);
        const query = /^\d{4}-\d{2}-\d{2}$/.test(date) ? `?date=${date}` : '';
        navigate(`/employees/${event.employeeId}${query}`);
    };

    if (loading) {
        return (
            <PanelBody>
                <RowSkeleton rows={6} />
            </PanelBody>
        );
    }

    if (!groups.length) {
        return (
            <PanelBody>
                <GhostRows />
                <EmptyState
                    title={t('pages.dashboard.feed.emptyTitle')}
                    hint={t('pages.dashboard.feed.emptyHint')}
                    sx={{ py: 0, flex: 'none' }}
                />
            </PanelBody>
        );
    }

    const [latestDate, latestEvents] = groups[0];

    return (
        <PanelBody>
            <DaySummary
                events={latestEvents}
                label={dayLabel(latestDate, language, t)}
                t={t}
            />

            <Box
                sx={{
                    maxHeight: DASH.feedMaxHeight,
                    overflowY: 'auto',
                    pr: 0.5,
                }}
            >
                {groups.map(([date, rows], groupIndex) => (
                    <Box key={date}>
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                bgcolor: 'background.paper',
                                py: 1,
                                mt: groupIndex === 0 ? 0 : 1,
                            }}
                        >
                            <Eyebrow>{dayLabel(date, language, t)}</Eyebrow>
                        </Box>
                        {rows.map((event) => (
                            <EventRow
                                key={event.id}
                                event={event}
                                t={t}
                                onOpen={openEvent}
                            />
                        ))}
                    </Box>
                ))}
            </Box>
        </PanelBody>
    );
};

export default ActivityFeed;
